import { collection, getCountFromServer, getDocs, query, where } from 'firebase/firestore';
import { getVestigingIdCandidates, resolveVestigingDocId, resolveVestigingIdWithData } from './resolveVestigingId';

export function expandLegacyBranchAliases(candidates = []) {
  const out = new Set((candidates || []).map((value) => String(value || '').trim()).filter(Boolean));
  const lowered = new Set(Array.from(out).map((value) => value.toLowerCase()));
  const heerlenAliases = ['y6yv2avw6ujwg7dbfm2q', 'heerlen', 'krinkels-heerlen', 'krinkels heerlen'];

  if (heerlenAliases.some((alias) => lowered.has(alias))) {
    heerlenAliases.forEach((alias) => out.add(alias));
    out.add('Krinkels Heerlen');
  }

  return Array.from(out);
}

async function readSubcollectionCountSafe(db, vestigingId, subcollectionName) {
  if (!vestigingId) return 0;
  try {
    const collRef = collection(db, 'vestigingen', vestigingId, subcollectionName);
    const countSnap = await getCountFromServer(collRef);
    return Number(countSnap.data()?.count || 0);
  } catch {
    try {
      const snap = await getDocs(collection(db, 'vestigingen', vestigingId, subcollectionName));
      return Number(snap.size || 0);
    } catch {
      return 0;
    }
  }
}

async function readTopLevelCountSafe(db, collectionName, candidates = []) {
  const ids = (candidates || []).map((value) => String(value || '').trim()).filter(Boolean);
  if (!ids.length) return 0;

  const fields = ['branchId', 'vestigingId', 'vestiging', 'branchName'];
  try {
    const counts = [];
    for (const field of fields) {
      for (const candidate of ids) {
        try {
          const q = query(collection(db, collectionName), where(field, '==', candidate));
          const countSnap = await getCountFromServer(q);
          counts.push(Number(countSnap.data()?.count || 0));
        } catch {
          try {
            const q = query(collection(db, collectionName), where(field, '==', candidate));
            const snap = await getDocs(q);
            counts.push(Number(snap.size || 0));
          } catch {
            counts.push(0);
          }
        }
      }
    }
    return Math.max(0, ...counts);
  } catch {
    return 0;
  }
}

export async function countBranchData(db, branch) {
  const [resolvedDocId, resolvedRobotsId, resolvedMeldingenId, resolvedOnderdelenId] = await Promise.all([
    resolveVestigingDocId(branch?.id),
    resolveVestigingIdWithData(branch?.id, 'robots'),
    resolveVestigingIdWithData(branch?.id, 'meldingen'),
    resolveVestigingIdWithData(branch?.id, 'onderdelen')
  ]);

  const vestigingCandidates = Array.from(new Set([
    resolvedDocId,
    resolvedRobotsId,
    resolvedMeldingenId,
    resolvedOnderdelenId,
    ...getVestigingIdCandidates(branch?.id),
    ...getVestigingIdCandidates(branch?.branchId)
  ]
    .map((value) => String(value || '').trim())
    .filter((value) => value && !value.includes('/'))));

  const topLevelCandidates = Array.from(new Set([
    branch?.id,
    branch?.branchId,
    branch?.name,
    branch?.naam,
    resolvedDocId,
    resolvedRobotsId,
    resolvedMeldingenId,
    resolvedOnderdelenId
  ]
    .map((value) => String(value || '').trim())
    .filter((value) => value && !value.includes('/'))));

  const [
    robotsSubCounts,
    meldingenSubCounts,
    autoMeldingenSubCounts,
    onderdelenSubCounts,
    robotsTop,
    meldingenTop,
    onderdelenTop
  ] = await Promise.all([
    Promise.all(vestigingCandidates.map((id) => readSubcollectionCountSafe(db, id, 'robots'))),
    Promise.all(vestigingCandidates.map((id) => readSubcollectionCountSafe(db, id, 'meldingen'))),
    Promise.all(vestigingCandidates.map((id) => readSubcollectionCountSafe(db, id, 'autoMeldingen'))),
    Promise.all(vestigingCandidates.map((id) => readSubcollectionCountSafe(db, id, 'onderdelen'))),
    readTopLevelCountSafe(db, 'robots', topLevelCandidates),
    readTopLevelCountSafe(db, 'meldingen', topLevelCandidates),
    readTopLevelCountSafe(db, 'onderdelen', topLevelCandidates)
  ]);

  const robotsCount = Math.max(0, ...(robotsSubCounts || []), robotsTop);
  const subMeldingenTotal = (meldingenSubCounts || []).map((count, index) => Number(count || 0) + Number((autoMeldingenSubCounts || [])[index] || 0));
  const meldingenCount = Math.max(0, ...(subMeldingenTotal || []), meldingenTop);
  const onderdelenCount = Math.max(0, ...(onderdelenSubCounts || []), onderdelenTop);

  return { robotsCount, meldingenCount, onderdelenCount, vestigingCandidates };
}
